// Estilos "prose" monocromos OTRIX compartidos por el editor WYSIWYG (TipTap) y el render
// público (ContentRenderer). Una sola fuente de verdad para que "lo que escribes" == "lo que ves".

const base = {
  color: 'text.primary',
  lineHeight: 1.7,
  fontSize: '0.95rem',
  wordBreak: 'break-word'
};

// Reglas por elemento (se aplican tanto en el editor como en la vista pública).
const elements = {
  '& h1': { fontSize: '1.6rem', fontWeight: 800, letterSpacing: '-0.02em', color: 'text.primary', mt: 3, mb: 1.25 },
  '& h2': { fontSize: '1.3rem', fontWeight: 800, letterSpacing: '-0.01em', color: 'text.primary', mt: 2.5, mb: 1 },
  '& h3': { fontSize: '1.1rem', fontWeight: 700, color: 'text.primary', mt: 2, mb: 0.75 },
  '& p': { my: 1.25 },
  '& a': { color: 'gold.main', textDecoration: 'underline', textDecorationColor: 'rgba(249, 189, 78, 0.5)' },
  '& strong': { color: 'text.primary', fontWeight: 700 },
  '& ul, & ol': { my: 1.25, pl: 3 },
  '& li': { my: 0.4 },
  '& img': { maxWidth: '100%', height: 'auto', borderRadius: '12px', border: '1px solid', borderColor: 'hairline.main', my: 1.5, display: 'block' },
  // Paridad con el editor: BlockNote serializa la alineación del bloque de TEXTO como
  // style="text-align: …" (y data-text-alignment); en media la perdía, así que el editor
  // la re-estampa como data-text-alignment sobre el propio elemento (o su <figure> cuando
  // hay caption). Como img/video/iframe son display:block, text-align no los mueve: se
  // alinean con márgenes automáticos.
  '& [data-text-alignment="center"], & [style*="text-align: center"]': { textAlign: 'center' },
  '& [data-text-alignment="right"], & [style*="text-align: right"]': { textAlign: 'right' },
  // Alineación directa de media (estampada por el editor): el elemento es display:block,
  // así que se centra/alinea con márgenes; requiere un ancho < 100% (imágenes redimensionadas).
  '& img[data-text-alignment="center"], & video[data-text-alignment="center"], & iframe[data-text-alignment="center"]': { marginLeft: 'auto', marginRight: 'auto' },
  '& img[data-text-alignment="right"], & video[data-text-alignment="right"], & iframe[data-text-alignment="right"]': { marginLeft: 'auto', marginRight: 0 },
  '& img[data-text-alignment="left"], & video[data-text-alignment="left"], & iframe[data-text-alignment="left"]': { marginLeft: 0, marginRight: 'auto' },
  // Figure (imagen con caption): se encoge al contenido para poder alinearse como bloque.
  '& figure[data-text-alignment="center"]': { width: 'fit-content', maxWidth: '100%', marginLeft: 'auto', marginRight: 'auto' },
  '& figure[data-text-alignment="right"]': { width: 'fit-content', maxWidth: '100%', marginLeft: 'auto', marginRight: 0 },
  '& figure[data-text-alignment="left"]': { width: 'fit-content', maxWidth: '100%', marginLeft: 0, marginRight: 'auto' },
  // Fallback legado: media dentro de un wrapper alineado (contenido viejo con el wrapper).
  '& [data-text-alignment="center"] img, & [style*="text-align: center"] img': { marginLeft: 'auto', marginRight: 'auto' },
  '& [data-text-alignment="right"] img, & [style*="text-align: right"] img': { marginLeft: 'auto', marginRight: 0 },
  // Multimedia del editor (video/audio subidos a S3).
  '& video': { width: '100%', maxWidth: '100%', borderRadius: '12px', border: '1px solid', borderColor: 'hairline.main', my: 1.5, display: 'block', backgroundColor: 'rgba(0,0,0,0.4)' },
  '& audio': { width: '100%', my: 1.5, display: 'block' },
  // Embeds whitelisteados (YouTube/Vimeo) convertidos a iframe en ContentRenderer.
  '& iframe': { width: '100%', aspectRatio: '16 / 9', border: '1px solid', borderColor: 'hairline.main', borderRadius: '12px', my: 1.5, display: 'block' },
  // Spotify (audio): reproductor compacto a ANCHO COMPLETO, NO 16:9.
  '& iframe[data-embed="spotify"]': { aspectRatio: 'auto', height: '152px', width: '100%', maxWidth: '100%' },
  // Contenedor del embed + link de respaldo ("Abrir en …").
  '& .otx-embed': { my: 1.5 },
  '& .otx-embed iframe': { my: 0 },
  '& a.otx-embed-link': {
    display: 'inline-flex', alignItems: 'center', gap: '4px', mt: 0.5,
    fontSize: '0.8rem', fontWeight: 600, color: 'gold.main', textDecoration: 'none'
  },
  // Archivo adjunto del editor (PDF y demás) reconstruido por convertFileBlocks.
  // BlockNote en modo lossless serializa el bloque `file` como un div con la URL SOLO en un
  // `data-url`, sin ancla, así que se veía pulsable (por su CSS) pero no abría nada. Aquí ya llega
  // convertido en un <a> de verdad, y se le da cuerpo de tarjeta para que se lea como algo
  // descargable y no como un enlace suelto en mitad del texto.
  '& a.otx-file': {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 1,
    maxWidth: '100%',
    my: 1,
    px: 1.5,
    py: 1,
    borderRadius: '12px',
    border: '1px solid',
    borderColor: 'hairline.main',
    backgroundColor: 'surface.overlay',
    color: 'text.primary',
    textDecoration: 'none',
    fontWeight: 600,
    fontSize: '0.9rem',
    transition: 'transform 200ms cubic-bezier(0.32, 0.72, 0, 1), border-color 200ms cubic-bezier(0.32, 0.72, 0, 1)',
    '&:hover': { borderColor: 'gold.main', transform: 'translateY(-1px)' },
    '&:active': { transform: 'scale(0.98)' },
    '&:focus-visible': { outline: '2px solid', outlineColor: 'gold.main', outlineOffset: '2px' }
  },
  // El icono va como texto para no depender de que el sanitizer deje pasar los atributos del SVG.
  '& a.otx-file .otx-file-icon': { color: 'gold.main', fontSize: '1.1rem', lineHeight: 1, flexShrink: 0 },
  '& a.otx-file .otx-file-name': { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  '& figure': { m: 0, my: 1.5 },
  '& figcaption': { fontSize: '0.8rem', color: 'text.secondary', textAlign: 'center', mt: 0.75 },
  // Cita editorial destacada: fondo suave + barra dorada + texto más grande.
  '& blockquote': {
    borderLeft: '3px solid',
    borderColor: 'gold.main',
    backgroundColor: 'surface.overlay',
    borderRadius: '0 10px 10px 0',
    px: 2,
    py: 1.25,
    my: 2,
    color: 'text.secondary',
    fontStyle: 'italic',
    fontSize: '1.05em'
  },
  '& code': { fontFamily: 'monospace', fontSize: '0.85em', backgroundColor: 'surface.overlay', px: 0.6, py: 0.2, borderRadius: '6px' },
  '& pre': { backgroundColor: 'surface.overlay', p: 1.5, borderRadius: '10px', overflowX: 'auto', border: '1px solid', borderColor: 'hairline.main', '& code': { backgroundColor: 'transparent', px: 0 } },
  '& hr': { border: 'none', borderTop: '1px solid', borderColor: 'hairline.main', my: 2 },
  // Lista plegable (toggle) → <details>/<summary> nativo. Flecha que rota al abrir/cerrar.
  '& details': { my: 1.25, borderRadius: '8px' },
  '& summary': {
    cursor: 'pointer',
    fontWeight: 700,
    listStyle: 'none',
    display: 'flex',
    alignItems: 'center',
    gap: '0.4em',
    '&::-webkit-details-marker': { display: 'none' },
    '&::before': { content: '"\\25B8"', color: 'gold.main', transition: 'transform 0.15s ease', display: 'inline-block' }
  },
  '& details[open] > summary::before': { transform: 'rotate(90deg)' },
  '& details > :not(summary)': { pl: 2.5 },
  '& table': { borderCollapse: 'collapse', width: '100%', my: 1.5 },
  '& th, & td': { border: '1px solid', borderColor: 'hairline.main', px: 1.25, py: 0.75, textAlign: 'left' },
  '& th': { backgroundColor: 'surface.overlay', color: 'text.primary', fontWeight: 700 }
};

// Estilos para la vista pública (ContentRenderer): se aplican directo al Box contenedor.
export const proseSx = {
  ...base,
  '& > :first-of-type': { mt: 0 },
  '& > :last-child': { mb: 0 },
  // Lead editorial: el primer párrafo del artículo se lee más grande y claro.
  '& > p:first-of-type': { fontSize: '1.1rem', color: 'text.primary', lineHeight: 1.75 },
  ...elements
};

// Estilos para el área editable de TipTap (.ProseMirror), incluyendo el placeholder.
export const editorContentSx = (minHeight) => ({
  '& .ProseMirror': {
    ...base,
    minHeight,
    outline: 'none',
    p: 2,
    '& > :first-of-type': { mt: 0 },
    ...elements,
    // Placeholder (extensión Placeholder) cuando el editor está vacío.
    '& p.is-editor-empty:first-of-type::before': {
      content: 'attr(data-placeholder)',
      float: 'left',
      color: 'text.disabled',
      pointerEvents: 'none',
      height: 0
    }
  }
});
